import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSearch,
  FiGrid,
  FiList,
  FiPlus,
  FiUser,
  FiX,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiStar,
  FiClock,
  FiTrendingUp,
  FiBookOpen,
  FiMapPin,
  FiGlobe,
  FiDollarSign,
  FiRefreshCw,
  FiSliders,
  FiKey,
} from "react-icons/fi";
import { useUserContext } from "../../context/UserContext";

// Custom Hooks
import {
  useWorkshops,
  useFeaturedWorkshops,
  useUpcomingWorkshops,
  useWorkshopStats,
  useWorkshopCategories,
  useDebounce,
} from "./hooks";

// Sub-components
import WSPWorkshopCard from "./subcomponents/WSPWorkshopCard";
import WSPFilterPanel from "./subcomponents/WSPFilterPanel";
import WSPEmptyState from "./subcomponents/WSPEmptyState";
import WSPLoadingSkeleton from "./subcomponents/WSPLoadingSkeleton";
import JoinByCodeModal from "./JoinByCodeModal";

// Utils
import { workshopApi } from "../../services/workshopApi";
import { DEFAULT_FILTERS, SORT_OPTIONS } from "./utils/constants";
import "./WorkshopsPage.css";

// ========================================
// SESSION STORAGE KEYS
// ========================================
const STORAGE_KEY = "workshops_page_state";

const getCachedState = () => {
  try {
    const cached = sessionStorage.getItem(STORAGE_KEY);
    if (!cached) return null;
    const { state, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > 30 * 60 * 1000) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return state;
  } catch {
    return null;
  }
};

const saveCachedState = (state) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ state, timestamp: Date.now() }));
  } catch {}
};

// ========================================
// ANIMATION VARIANTS
// ========================================
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1, y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

/**
 * WorkshopsPage - Premium Redesign
 * Clean, spacious, modern discovery layout
 * Matches the quality of interior workshop detail pages
 */
const WorkshopsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserContext();
  const cachedState = getCachedState();

  // ========================================
  // STATE
  // ========================================
  const [searchQuery, setSearchQuery] = useState(cachedState?.searchQuery || "");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [filters, setFilters] = useState(cachedState?.filters || DEFAULT_FILTERS);
  const [currentPage, setCurrentPage] = useState(cachedState?.currentPage || 1);
  const [viewMode, setViewMode] = useState(cachedState?.viewMode || "grid");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(cachedState?.activeTab || "all");
  const [showJoinByCode, setShowJoinByCode] = useState(false);

  // ========================================
  // DATA FETCHING
  // ========================================
  const { workshops, loading, error, pagination, refetch } = useWorkshops({
    query: debouncedSearchQuery,
    ...filters,
    page: currentPage,
    limit: 12,
  });

  const { featuredWorkshops, loading: featuredLoading } = useFeaturedWorkshops(6);
  const { upcomingWorkshops, loading: upcomingLoading } = useUpcomingWorkshops(8);
  const { stats, loading: statsLoading } = useWorkshopStats();
  const { categories } = useWorkshopCategories();

  // ========================================
  // PERSIST STATE
  // ========================================
  useEffect(() => {
    saveCachedState({ searchQuery, filters, currentPage, viewMode, activeTab });
  }, [searchQuery, filters, currentPage, viewMode, activeTab]);

  // ========================================
  // COMPUTED VALUES
  // ========================================
  const hasActiveFilters = useMemo(() => {
    return searchQuery || Object.entries(filters).some(
      ([key, value]) => value !== null && value !== "all" && !["sortBy", "sortOrder"].includes(key)
    );
  }, [searchQuery, filters]);

  const activeFilterCount = useMemo(() => {
    return Object.entries(filters).filter(
      ([key, value]) => value !== null && value !== "all" && !["sortBy", "sortOrder"].includes(key)
    ).length;
  }, [filters]);

  // Optimistic updates
  const [joiningWorkshopId, setJoiningWorkshopId] = useState(null);
  const [joinedWorkshops, setJoinedWorkshops] = useState({});

  const applyOptimisticUpdates = useCallback((workshopList) => {
    if (!workshopList) return workshopList;
    return workshopList.map(w => {
      const update = joinedWorkshops[w._id];
      if (update) {
        return {
          ...w,
          userRole: {
            ...w.userRole,
            isParticipant: true,
            participantStatus: update.status,
            shouldShowApplyButton: false,
          },
          participantCount: (w.participantCount || 0) + (update.isInstantJoin ? 1 : 0),
        };
      }
      return w;
    });
  }, [joinedWorkshops]);

  const displayedWorkshops = useMemo(() => {
    let result;
    if (activeTab === "featured") result = featuredWorkshops;
    else if (activeTab === "upcoming") result = upcomingWorkshops;
    else result = workshops;
    return applyOptimisticUpdates(result);
  }, [activeTab, workshops, featuredWorkshops, upcomingWorkshops, applyOptimisticUpdates]);

  const isLoading = useMemo(() => {
    if (activeTab === "featured") return featuredLoading;
    if (activeTab === "upcoming") return upcomingLoading;
    return loading;
  }, [activeTab, loading, featuredLoading, upcomingLoading]);

  // ========================================
  // EVENT HANDLERS
  // ========================================
  const handleSearch = (e) => {
    e?.preventDefault?.();
    setCurrentPage(1);
  };

  const handleFilterChange = (filterKey, value) => {
    setFilters((prev) => ({ ...prev, [filterKey]: value }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchQuery("");
    setCurrentPage(1);
  };

  const handleApplyToWorkshop = async (workshopId, isPaid = false) => {
    if (!user) {
      alert("Please login to apply to workshops");
      navigate("/login");
      return;
    }

    setJoiningWorkshopId(workshopId);

    try {
      if (isPaid) {
        setJoiningWorkshopId(null);
        navigate(`/workshops/${workshopId}/payment`);
      } else {
        const response = await workshopApi.applyToWorkshop(workshopId);

        if (response.success) {
          const status = response.status || response.data?.status || response.participant?.status;
          const message = response.message?.toLowerCase() || "";
          const isInstantJoin =
            status === "approved" || status === "joined" ||
            message.includes("approved") || message.includes("joined") || message.includes("enrolled") ||
            response.autoApproved === true || response.data?.autoApproved === true;

          setJoinedWorkshops(prev => ({
            ...prev,
            [workshopId]: { status: isInstantJoin ? "approved" : "pending", isInstantJoin },
          }));

          if (isInstantJoin) {
            alert("🎉 Successfully joined! You're now enrolled in this workshop.");
          } else {
            alert("✅ Application submitted! Please wait for organizer approval.");
          }
        }
      }
    } catch (err) {
      alert(err.message || "Failed to apply to workshop");
    } finally {
      setJoiningWorkshopId(null);
    }
  };

  const handleCategoryClick = (categoryName) => {
    const newValue = filters.category === categoryName ? "all" : categoryName;
    handleFilterChange("category", newValue);
    setActiveTab("all");
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  // ========================================
  // RENDER
  // ========================================
  return (
    <div className="wsp-page">
      {/* ========================================
          HERO - Clean Header
          ======================================== */}
      <motion.header
        className="wsp-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="wsp-hero-content">
          {/* Top Bar: Brand + Search + Actions */}
          <motion.div
            className="wsp-hero-header"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Brand */}
            <div className="wsp-hero-brand">
              <div className="wsp-hero-icon">
                <FiBookOpen size={18} />
              </div>
              <div className="wsp-hero-text">
                <h1>Workshops</h1>
                <p>Discover, learn & collaborate</p>
              </div>
            </div>

            {/* Search */}
            <div className="wsp-search-bar">
              <FiSearch size={16} />
              <input
                type="text"
                placeholder="Search workshops..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <motion.button
                  className="wsp-search-clear"
                  onClick={() => setSearchQuery("")}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <FiX size={12} />
                </motion.button>
              )}
            </div>

            {/* Actions */}
            <div className="wsp-hero-actions">
              <div className="wsp-nav-toggle">
                <button
                  className={`wsp-nav-btn ${activeTab !== "my" ? "active" : ""}`}
                  onClick={() => setActiveTab("all")}
                >
                  <FiGrid size={14} />
                  <span>Discover</span>
                </button>
                <button
                  className="wsp-nav-btn"
                  onClick={() => navigate("/workshops/my-workshops")}
                >
                  <FiUser size={14} />
                  <span>My Workshops</span>
                </button>
              </div>
              <motion.button
                className="wsp-join-code-btn"
                onClick={() => setShowJoinByCode(true)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiKey size={14} />
                <span>Join</span>
              </motion.button>
              <motion.button
                className="wsp-create-btn"
                onClick={() => navigate("/workshops/create")}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <FiPlus size={14} />
                <span>Create</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Tabs + Stats */}
          <motion.div
            className="wsp-stats-tabs-row"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            {/* Tabs - Like Interior Detail Page */}
            <div className="wsp-tabs">
              <button
                className={`wsp-tab ${activeTab === "all" ? "active" : ""}`}
                onClick={() => setActiveTab("all")}
              >
                <FiGrid size={14} />
                <span>All</span>
                {pagination && <span className="wsp-tab-count">{pagination.totalItems}</span>}
              </button>
              <button
                className={`wsp-tab ${activeTab === "featured" ? "active" : ""}`}
                onClick={() => setActiveTab("featured")}
              >
                <FiStar size={14} />
                <span>Featured</span>
                <span className="wsp-tab-count">{featuredWorkshops?.length || 0}</span>
              </button>
              <button
                className={`wsp-tab ${activeTab === "upcoming" ? "active" : ""}`}
                onClick={() => setActiveTab("upcoming")}
              >
                <FiClock size={14} />
                <span>Upcoming</span>
                <span className="wsp-tab-count">{upcomingWorkshops?.length || 0}</span>
              </button>
            </div>

            {/* Minimal Inline Stats */}
            <div className="wsp-inline-stats">
              <div className="wsp-stat-mini">
                <FiBookOpen size={14} className="wsp-stat-icon blue" />
                <span className="wsp-stat-num">{stats?.totalWorkshops || 0}</span>
                <span className="wsp-stat-txt">Total</span>
              </div>
              <div className="wsp-stat-divider" />
              <div className="wsp-stat-mini">
                <FiTrendingUp size={14} className="wsp-stat-icon teal" />
                <span className="wsp-stat-num">{stats?.ongoingWorkshops || 0}</span>
                <span className="wsp-stat-txt">Active</span>
              </div>
              <div className="wsp-stat-divider" />
              <div className="wsp-stat-mini">
                <FiClock size={14} className="wsp-stat-icon orange" />
                <span className="wsp-stat-num">{upcomingWorkshops?.length || 0}</span>
                <span className="wsp-stat-txt">Upcoming</span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.header>

      {/* ========================================
          FILTER BAR
          ======================================== */}
      <div className="wsp-filter-section">
        <motion.div
          className="wsp-filter-bar"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.15 }}
        >
          <div className="wsp-filter-pills">
            {[
              { key: "pricing", value: "free", label: "Free", icon: FiDollarSign },
              { key: "format", value: "online", label: "Online", icon: FiGlobe },
              { key: "format", value: "physical", label: "In-Person", icon: FiMapPin },
            ].map((filter) => (
              <motion.button
                key={`${filter.key}-${filter.value}`}
                className={`wsp-pill ${filters[filter.key] === filter.value ? "active" : ""}`}
                onClick={() =>
                  handleFilterChange(filter.key, filters[filter.key] === filter.value ? "all" : filter.value)
                }
                whileTap={{ scale: 0.97 }}
              >
                <filter.icon size={13} />
                <span>{filter.label}</span>
              </motion.button>
            ))}
            <motion.button
              className={`wsp-pill wsp-pill-filter ${isFilterOpen ? "active" : ""} ${activeFilterCount > 0 ? "has-count" : ""}`}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              whileTap={{ scale: 0.97 }}
            >
              <FiSliders size={13} />
              <span>More</span>
              {activeFilterCount > 0 && <span className="wsp-pill-count">{activeFilterCount}</span>}
            </motion.button>
          </div>

          <div className="wsp-filter-right">
            <div className="wsp-sort">
              <select
                className="wsp-sort-select"
                value={filters.sortBy}
                onChange={(e) => handleFilterChange("sortBy", e.target.value)}
              >
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <FiChevronDown className="wsp-sort-chevron" size={13} />
            </div>

            <div className="wsp-view-btns">
              <button
                className={`wsp-view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
              >
                <FiGrid size={15} />
              </button>
              <button
                className={`wsp-view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
              >
                <FiList size={15} />
              </button>
            </div>
          </div>
        </motion.div>

        {/* Active Filters Strip */}
        <AnimatePresence>
          {hasActiveFilters && (
            <motion.div
              className="wsp-active-filters"
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: "auto", marginTop: 10 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
            >
              <span className="wsp-active-label">Filters:</span>
              {searchQuery && (
                <span className="wsp-filter-chip">
                  "{searchQuery}"
                  <button onClick={() => setSearchQuery("")}><FiX size={11} /></button>
                </span>
              )}
              {Object.entries(filters).map(([key, value]) => {
                if (value && value !== "all" && !["sortBy", "sortOrder"].includes(key)) {
                  return (
                    <span key={key} className="wsp-filter-chip">
                      {value}
                      <button onClick={() => handleFilterChange(key, "all")}><FiX size={11} /></button>
                    </span>
                  );
                }
                return null;
              })}
              <button className="wsp-clear-all" onClick={handleClearFilters}>
                <FiRefreshCw size={11} /> Clear All
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Filter Panel (Collapsible) */}
      <AnimatePresence>
        {isFilterOpen && (
          <WSPFilterPanel
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            onClose={() => setIsFilterOpen(false)}
            categories={categories}
          />
        )}
      </AnimatePresence>

      {/* ========================================
          MAIN CONTENT
          ======================================== */}
      <main className="wsp-main">
        <div className="wsp-container">
          <section className="wsp-feed-full">
            {isLoading ? (
              <WSPLoadingSkeleton count={6} viewMode={viewMode} />
            ) : error ? (
              <motion.div
                className="wsp-error-state"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                <div className="wsp-error-icon">⚠️</div>
                <h3>Something went wrong</h3>
                <p>{error.message || "Failed to load workshops"}</p>
                <button className="wsp-retry-btn" onClick={refetch}>
                  <FiRefreshCw size={14} /> Try Again
                </button>
              </motion.div>
            ) : displayedWorkshops?.length === 0 ? (
              <WSPEmptyState
                hasFilters={hasActiveFilters}
                onClearFilters={handleClearFilters}
              />
            ) : (
              <>
                <motion.div
                  className={`wsp-workshop-grid-3col ${viewMode === "list" ? "wsp-list-view" : ""}`}
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {displayedWorkshops?.map((workshop) => (
                    <motion.div key={workshop._id} variants={itemVariants}>
                      <WSPWorkshopCard
                        workshop={workshop}
                        onApply={handleApplyToWorkshop}
                        currentUser={user}
                        viewMode={viewMode}
                        joiningWorkshopId={joiningWorkshopId}
                      />
                    </motion.div>
                  ))}
                </motion.div>

                {/* Pagination */}
                {activeTab === "all" && pagination && pagination.totalPages > 1 && (
                  <motion.div
                    className="wsp-pagination"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <button
                      className="wsp-page-btn wsp-page-nav"
                      disabled={!pagination.hasPreviousPage}
                      onClick={() => handlePageChange(currentPage - 1)}
                    >
                      <FiChevronLeft size={16} />
                    </button>

                    {Array.from(
                      { length: Math.min(5, pagination.totalPages) },
                      (_, i) => {
                        let pageNum;
                        if (pagination.totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= pagination.totalPages - 2) {
                          pageNum = pagination.totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            className={`wsp-page-btn ${currentPage === pageNum ? "active" : ""}`}
                            onClick={() => handlePageChange(pageNum)}
                          >
                            {pageNum}
                          </button>
                        );
                      }
                    )}

                    <button
                      className="wsp-page-btn wsp-page-nav"
                      disabled={!pagination.hasNextPage}
                      onClick={() => handlePageChange(currentPage + 1)}
                    >
                      <FiChevronRight size={16} />
                    </button>
                  </motion.div>
                )}
              </>
            )}
          </section>
        </div>
      </main>

      {/* Join by Code Modal */}
      <JoinByCodeModal
        isOpen={showJoinByCode}
        onClose={() => setShowJoinByCode(false)}
      />
    </div>
  );
};

export default WorkshopsPage;
